import React, { useState, useEffect } from 'react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import portalAPI from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';
import './PortalAPIIntegration.css';

interface APIKey {
  id: number;
  name: string;
  key_type: 'live' | 'test';
  masked_key: string;
  is_active: boolean;
  permissions: {
    customer_lookup?: boolean;
    purchases?: boolean;
    inventory?: boolean;
    webhooks?: boolean;
  };
  rate_limit_per_minute: number;
  last_used_at: string | null;
  request_count: number;
  created_at: string;
}

interface Webhook {
  id: number;
  webhook_url: string;
  events: string[];
  is_active: boolean;
  last_triggered_at: string | null;
  failure_count: number;
  created_at: string;
}

interface SyncLog {
  id: number;
  timestamp: string;
  endpoint: string;
  method: string;
  status_code: number;
  response_time_ms: number;
  error_message?: string;
}

const POS_SYSTEMS = [
  { id: 'epicor-eagle', name: 'Epicor Eagle', description: 'Enterprise retail management for farm & ranch' },
  { id: 'square', name: 'Square', description: 'Modern POS with easy API integration' },
  { id: 'clover', name: 'Clover', description: 'Flexible POS platform' },
  { id: 'paladin', name: 'Paladin Data', description: 'Hardware store & farm supply POS' },
  { id: 'lightspeed', name: 'Lightspeed', description: 'Retail and restaurant POS' },
  { id: 'agvance', name: 'Agvance', description: 'Agricultural supply chain management' },
  { id: 'other', name: 'Other / Custom', description: 'Custom integration using our REST API' },
];

const PortalAPIIntegration: React.FC = () => {
  usePortalAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // API Keys
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyType, setNewKeyType] = useState<'live' | 'test'>('live');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  // Webhooks
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>(['purchase.created', 'member.updated']);
  const [webhookKeyId, setWebhookKeyId] = useState<number | null>(null);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);

  // POS Selection
  const [selectedPOS, setSelectedPOS] = useState<string>('');

  // Sync Logs
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);

  // Test Connection
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // API Documentation
  const [showDocs, setShowDocs] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load API keys
      const keysResponse = await portalAPI.get('/store/api-keys/');
      setApiKeys(keysResponse.data.api_keys || []);

      // Load webhooks
      const webhooksResponse = await portalAPI.get('/store/webhooks/');
      setWebhooks(webhooksResponse.data.webhooks || []);

      // Load sync logs
      try {
        const logsResponse = await portalAPI.get('/store/purchase-sync-log/');
        setSyncLogs(logsResponse.data.logs || []);
      } catch {
        // Sync logs might not exist yet
        setSyncLogs([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load API integration data');
    } finally {
      setLoading(false);
    }
  };

  const createAPIKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    try {
      setCreatingKey(true);
      setError('');

      const response = await portalAPI.post('/store/api-keys/', {
        name: newKeyName,
        key_type: newKeyType,
      });

      if (response.data.success) {
        setNewlyCreatedKey(response.data.api_key.key);
        setSuccess('API key created successfully. Copy the key now - it will not be shown again!');
        setShowCreateKey(false);
        setNewKeyName('');
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeAPIKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await portalAPI.delete(`/store/api-keys/${keyId}/`);
      setSuccess('API key revoked successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke API key');
    }
  };

  const createWebhook = async () => {
    if (!webhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }
    if (!webhookKeyId) {
      setError('Please select an API key for this webhook');
      return;
    }

    try {
      setCreatingWebhook(true);
      setError('');

      const response = await portalAPI.post('/store/webhooks/', {
        api_key_id: webhookKeyId,
        webhook_url: webhookUrl,
        events: webhookEvents,
      });

      if (response.data.success) {
        setNewWebhookSecret(response.data.webhook.secret);
        setSuccess('Webhook created successfully. Copy the secret now - it will not be shown again!');
        setShowCreateWebhook(false);
        setWebhookUrl('');
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create webhook');
    } finally {
      setCreatingWebhook(false);
    }
  };

  const testConnection = async () => {
    if (apiKeys.filter(k => k.is_active).length === 0) {
      setError('Create an API key first to test the connection');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      const response = await portalAPI.get('/store/api-keys/');

      setTestResult({
        success: true,
        message: `Connection successful! Found ${response.data.api_keys?.length || 0} API key(s).`
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-content">
          <div className="loading-state">Loading API integration settings...</div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-content">
        <TutorialAutoStart pageKey="api-integration" />
        <div className="api-integration-page">
          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1>Partner API Integration</h1>
              <p className="page-description">
                Connect your POS system to automatically sync Bridge Club member purchases and loyalty rewards.
              </p>
            </div>
            <TutorialHelpButton pageKey="api-integration" />
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Newly Created Key Display */}
          {newlyCreatedKey && (
            <div className="new-key-banner">
              <div className="new-key-header">
                <span className="key-icon">&#128273;</span>
                <strong>Your New API Key</strong>
              </div>
              <p className="key-warning">Copy this key now. It will not be shown again!</p>
              <div className="key-display">
                <code>{newlyCreatedKey}</code>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                >
                  Copy
                </button>
              </div>
              <button
                className="btn btn-sm"
                onClick={() => setNewlyCreatedKey(null)}
              >
                I've saved it
              </button>
            </div>
          )}

          {/* Newly Created Webhook Secret Display */}
          {newWebhookSecret && (
            <div className="new-key-banner">
              <div className="new-key-header">
                <span className="key-icon">&#128274;</span>
                <strong>Your Webhook Secret</strong>
              </div>
              <p className="key-warning">Copy this secret now. It will not be shown again!</p>
              <div className="key-display">
                <code>{newWebhookSecret}</code>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => copyToClipboard(newWebhookSecret)}
                >
                  Copy
                </button>
              </div>
              <button
                className="btn btn-sm"
                onClick={() => setNewWebhookSecret(null)}
              >
                I've saved it
              </button>
            </div>
          )}

          {/* POS Selection */}
          <section className="api-section">
            <h2>1. Select Your POS System</h2>
            <div className="pos-grid" data-tutorial="pos-selector">
              {POS_SYSTEMS.map(pos => (
                <div
                  key={pos.id}
                  className={`pos-card ${selectedPOS === pos.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPOS(pos.id)}
                >
                  <div className="pos-name">{pos.name}</div>
                  <div className="pos-description">{pos.description}</div>
                </div>
              ))}
            </div>

            {selectedPOS && (
              <div className="pos-instructions">
                <h3>Setup Instructions for {POS_SYSTEMS.find(p => p.id === selectedPOS)?.name}</h3>
                {selectedPOS === 'epicor-eagle' && (
                  <ol>
                    <li>In Eagle, go to Admin &gt; System &gt; API Configuration</li>
                    <li>Add a new webhook endpoint for Red Bridge Feed</li>
                    <li>Paste your API key in the authentication field</li>
                    <li>Configure purchase events to POST to our endpoint</li>
                    <li>Contact your Eagle rep if you need the Bridge Club integration module</li>
                  </ol>
                )}
                {selectedPOS === 'square' && (
                  <ol>
                    <li>In Square Dashboard, go to Apps &gt; My Apps</li>
                    <li>Create a new application or use existing</li>
                    <li>Under Webhooks, add endpoint: <code>https://api.redbridgefeed.com/partner/v1/purchase/</code></li>
                    <li>Set the API key header: <code>X-Partner-Key: your_api_key_here</code></li>
                    <li>Subscribe to PAYMENT_COMPLETED events</li>
                  </ol>
                )}
                {selectedPOS === 'clover' && (
                  <ol>
                    <li>Log into your Clover Web Dashboard</li>
                    <li>Go to More Tools &gt; REST API</li>
                    <li>Create webhook for order.created events</li>
                    <li>Use our endpoint URL with your API key in headers</li>
                  </ol>
                )}
                {selectedPOS === 'paladin' && (
                  <ol>
                    <li>Contact Paladin support for API integration setup</li>
                    <li>They will help configure the Red Bridge Feed connector</li>
                    <li>Provide them your API key and endpoint URL</li>
                  </ol>
                )}
                {selectedPOS === 'lightspeed' && (
                  <ol>
                    <li>In Lightspeed, go to Settings &gt; Integrations</li>
                    <li>Add custom webhook integration</li>
                    <li>Configure for sale.complete events</li>
                    <li>Add API key to authorization header</li>
                  </ol>
                )}
                {selectedPOS === 'agvance' && (
                  <ol>
                    <li>Contact your SSI representative</li>
                    <li>Request the Red Bridge Feed integration module</li>
                    <li>Provide your API credentials for configuration</li>
                  </ol>
                )}
                {selectedPOS === 'other' && (
                  <div>
                    <p>Use our REST API directly:</p>
                    <ul>
                      <li><strong>Customer Lookup:</strong> <code>GET /partner/v1/customer/&#123;phone_or_email&#125;/</code></li>
                      <li><strong>Submit Purchase:</strong> <code>POST /partner/v1/purchase/</code></li>
                      <li><strong>Check Inventory:</strong> <code>GET /partner/v1/inventory/</code></li>
                      <li><strong>Product List:</strong> <code>GET /partner/v1/products/</code></li>
                    </ul>
                    <p>Include your API key in the <code>X-Partner-Key</code> header.</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* API Keys */}
          <section className="api-section" data-tutorial="api-keys-section">
            <div className="section-header">
              <h2>2. API Keys</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateKey(true)}
              >
                + Create API Key
              </button>
            </div>

            {showCreateKey && (
              <div className="create-form">
                <h3>Create New API Key</h3>
                <div className="form-group">
                  <label>Key Name</label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g., Production POS Integration"
                  />
                </div>
                <div className="form-group">
                  <label>Key Type</label>
                  <select value={newKeyType} onChange={(e) => setNewKeyType(e.target.value as 'live' | 'test')}>
                    <option value="live">Live (Production)</option>
                    <option value="test">Test (Sandbox)</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={createAPIKey}
                    disabled={creatingKey}
                  >
                    {creatingKey ? 'Creating...' : 'Create Key'}
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowCreateKey(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {apiKeys.length === 0 ? (
              <div className="empty-state">
                <p>No API keys yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="api-keys-list">
                {apiKeys.map(key => (
                  <div key={key.id} className={`api-key-card ${!key.is_active ? 'revoked' : ''}`}>
                    <div className="key-header">
                      <div className="key-name">{key.name}</div>
                      <span className={`key-type-badge ${key.key_type}`}>
                        {key.key_type === 'live' ? 'LIVE' : 'TEST'}
                      </span>
                      {!key.is_active && <span className="revoked-badge">REVOKED</span>}
                    </div>
                    <div className="key-value">
                      <code>{key.masked_key}</code>
                    </div>
                    <div className="key-stats">
                      <span>Requests: {key.request_count.toLocaleString()}</span>
                      <span>Last used: {formatDate(key.last_used_at)}</span>
                      <span>Created: {formatDate(key.created_at)}</span>
                    </div>
                    {key.is_active && (
                      <div className="key-actions">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => revokeAPIKey(key.id)}
                        >
                          Revoke Key
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Webhooks */}
          <section className="api-section" data-tutorial="webhooks-section">
            <div className="section-header">
              <h2>3. Webhooks (Optional)</h2>
              <button
                className="btn btn-outline"
                onClick={() => setShowCreateWebhook(true)}
                disabled={apiKeys.filter(k => k.is_active).length === 0}
              >
                + Add Webhook
              </button>
            </div>
            <p className="section-description">
              Receive real-time notifications when events occur (e.g., member earns a reward).
            </p>

            {showCreateWebhook && (
              <div className="create-form">
                <h3>Create Webhook</h3>
                <div className="form-group">
                  <label>Webhook URL</label>
                  <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://your-server.com/webhook"
                  />
                </div>
                <div className="form-group">
                  <label>API Key</label>
                  <select
                    value={webhookKeyId || ''}
                    onChange={(e) => setWebhookKeyId(Number(e.target.value) || null)}
                  >
                    <option value="">Select API Key</option>
                    {apiKeys.filter(k => k.is_active).map(key => (
                      <option key={key.id} value={key.id}>{key.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Events</label>
                  <div className="checkbox-group">
                    {['purchase.created', 'member.updated', 'reward.earned', 'reward.redeemed'].map(event => (
                      <label key={event} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={webhookEvents.includes(event)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWebhookEvents([...webhookEvents, event]);
                            } else {
                              setWebhookEvents(webhookEvents.filter(ev => ev !== event));
                            }
                          }}
                        />
                        {event}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={createWebhook}
                    disabled={creatingWebhook}
                  >
                    {creatingWebhook ? 'Creating...' : 'Create Webhook'}
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowCreateWebhook(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {webhooks.length > 0 && (
              <div className="webhooks-list">
                {webhooks.map(webhook => (
                  <div key={webhook.id} className="webhook-card">
                    <div className="webhook-url">{webhook.webhook_url}</div>
                    <div className="webhook-events">
                      {webhook.events.map(event => (
                        <span key={event} className="event-badge">{event}</span>
                      ))}
                    </div>
                    <div className="webhook-stats">
                      <span>Last triggered: {formatDate(webhook.last_triggered_at)}</span>
                      {webhook.failure_count > 0 && (
                        <span className="failure-count">Failures: {webhook.failure_count}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Test Connection */}
          <section className="api-section">
            <div className="section-header">
              <h2>4. Test Connection</h2>
              <button
                className="btn btn-outline"
                onClick={testConnection}
                disabled={testing}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                {testResult.success ? '&#10003;' : '&#10007;'} {testResult.message}
              </div>
            )}
          </section>

          {/* Recent API Calls */}
          <section className="api-section">
            <h2>5. Recent API Activity</h2>
            {syncLogs.length === 0 ? (
              <div className="empty-state">
                <p>No API activity yet. Once you integrate, API calls will appear here.</p>
              </div>
            ) : (
              <div className="sync-logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Response Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncLogs.slice(0, 20).map(log => (
                      <tr key={log.id} className={log.status_code >= 400 ? 'error-row' : ''}>
                        <td>{formatDate(log.timestamp)}</td>
                        <td><code>{log.endpoint}</code></td>
                        <td>{log.method}</td>
                        <td>
                          <span className={`status-badge status-${Math.floor(log.status_code / 100)}xx`}>
                            {log.status_code}
                          </span>
                        </td>
                        <td>{log.response_time_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* API Documentation */}
          <section id="api-docs-section" className="api-section" data-tutorial="api-docs">
            <div className="section-header-row">
              <h2>API Documentation</h2>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowDocs(!showDocs)}
              >
                {showDocs ? 'Hide Documentation' : 'Show Documentation'}
              </button>
            </div>

            {showDocs && (
              <div className="api-docs-inline">
                <div className="docs-block">
                  <h3>Base URL</h3>
                  <code className="code-display">https://api.redbridgefeed.com/api/partner/v1/</code>
                </div>

                <div className="docs-block">
                  <h3>Authentication</h3>
                  <p>Include your API key in the header:</p>
                  <code className="code-display">X-API-Key: rbf_live_your_api_key_here</code>
                </div>

                <div className="docs-block">
                  <h3>Endpoints</h3>

                  <div className="endpoint-doc">
                    <div className="endpoint-title">
                      <span className="method post">POST</span>
                      <code>/purchase/</code>
                      <span className="endpoint-desc">Record a member purchase</span>
                    </div>
                    <p>Submit Bridge Club member purchases to earn loyalty rewards.</p>
                    <h4>Request Body</h4>
                    <pre>{`{
  "member_code": "ABC123",
  "amount": 45.99,
  "items": [
    {"sku": "FEED-001", "name": "Chicken Feed 50lb", "qty": 1, "price": 22.99},
    {"sku": "FEED-002", "name": "Horse Feed 40lb", "qty": 1, "price": 23.00}
  ],
  "transaction_id": "POS-12345"
}`}</pre>
                  </div>

                  <div className="endpoint-doc">
                    <div className="endpoint-title">
                      <span className="method get">GET</span>
                      <code>/member/{'{member_code}'}/ </code>
                      <span className="endpoint-desc">Look up member info</span>
                    </div>
                    <p>Get member details including points balance and tier status.</p>
                    <h4>Response</h4>
                    <pre>{`{
  "member_code": "ABC123",
  "name": "John Smith",
  "points_balance": 1250,
  "tier": "Gold",
  "lifetime_purchases": 2500.00
}`}</pre>
                  </div>

                  <div className="endpoint-doc">
                    <div className="endpoint-title">
                      <span className="method get">GET</span>
                      <code>/inventory/</code>
                      <span className="endpoint-desc">Get available products</span>
                    </div>
                    <p>Retrieve list of Bridge Club products for your store.</p>
                  </div>
                </div>

                <div className="docs-block">
                  <h3>Rate Limits</h3>
                  <p>Default: 60 requests per minute per API key. Contact us if you need higher limits.</p>
                </div>
              </div>
            )}

          </section>

          {/* Need Help? Section */}
          <section className="need-help-section">
            <h3>Need Help?</h3>
            <p>
              View our full{' '}
              <button
                type="button"
                className="help-link"
                onClick={() => {
                  setShowDocs(true);
                  document.getElementById('api-docs-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                API Documentation
              </button>
              {' '}or contact{' '}
              <a href="mailto:support@theredbridgefarm.com" className="help-link">
                support@theredbridgefarm.com
              </a>
              {' '}for integration assistance.
            </p>
          </section>
        </div>
      </div>
      <PortalFooter />
    </div>
  );
};

export default PortalAPIIntegration;
